import { FC, useCallback, useState } from 'react';
import styled from '@emotion/styled';
import { FormattedMessage } from 'react-intl';

import { rem } from '@app/styles/theme/layout';

import useCart from '../../hooks/useCart';
import { Button } from '../../components/Button';
import { CartGridCell, BREAKPOINT_WIDE } from './Cart.styles';
import messages from './CartCoupon.messages';

import { CartQuery_magento2_cart_appliedCoupons } from '../../context/__generated__/CartQuery';

const CartCoupon: FC = () => (
  <>
    <AppliedCoupons />
    <CouponForm />
  </>
);

export default CartCoupon;

const AppliedCoupons: FC = () => {
  const { coupons, loadingCoupon: loading } = useCart();
  if (loading || !coupons || !coupons.length) return null;
  return (
    <>
      {coupons.map((coupon) => {
        return coupon ? <Coupon key={coupon.code} code={coupon.code} /> : null;
      })}
    </>
  );
};

const Coupon: FC<{
  code: CartQuery_magento2_cart_appliedCoupons['code'];
}> = ({ code }) => {
  const { removeCoupon } = useCart();

  return (
    <>
      <CartGridCell hAlign="center" />
      <CouponContentCell data-testid="coupon-applied">
        <h4 data-testid={`coupon-applied-${code}-label`}>
          <FormattedMessage {...messages.coupon} /> ({code})
        </h4>
        <RemoveButton
          type="transparent"
          data-testid="coupon-applied-remove"
          onClick={removeCoupon}
        >
          <FormattedMessage {...messages.removeCoupon} />
        </RemoveButton>
      </CouponContentCell>
    </>
  );
};

const CouponForm: FC = () => {
  const { applyCoupon, loadingCoupon: loading } = useCart();
  const [coupon, setCoupon] = useState('');

  const handleSubmit = useCallback(async () => {
    if (!coupon) return;

    await applyCoupon(coupon);
    setCoupon('');
  }, [applyCoupon, coupon]);

  return (
    <>
      <CartGridCell hAlign="center" />
      <CartGridCell>
        <h4>
          <FormattedMessage {...messages.coupon} />:
        </h4>
      </CartGridCell>
      <InputCell>
        <Input
          data-testid="coupon-form-input"
          type="text"
          value={coupon}
          disabled={loading}
          onChange={(e) => setCoupon(e.target.value)}
        />
      </InputCell>
      <ButtonCell>
        <Button
          data-testid="coupon-form-submit"
          type="primary"
          disabled={loading || !coupon}
          onClick={handleSubmit}
        >
          <FormattedMessage {...messages.applyCoupon} />
        </Button>
      </ButtonCell>
    </>
  );
};

const RemoveButton = styled(Button)`
  padding: 0;
`;

const InputCell = styled(CartGridCell)`
  padding-right: 0;
  grid-column: 4 / 5;

  @media (min-width: ${BREAKPOINT_WIDE}) {
    grid-column: 3 / 4;
  }
`;

const ButtonCell = styled(CartGridCell)`
  padding-right: 0;
  grid-column: 5 / 6;

  @media (min-width: ${BREAKPOINT_WIDE}) {
    grid-column: 4 / 6;
  }
`;

const CouponContentCell = styled(CartGridCell)`
  grid-column: 2 / 6;
`;

const Input = styled.input`
  display: block;
  width: 100%;
  max-width: ${rem(164)};
  padding: 5px;
`;
